/**
 * Bluesky video upload service
 *
 * Implements the correct upload flow for self-hosted PDSes:
 * 1. Get a short-lived service auth token from the user's own PDS
 * 2. POST the raw video bytes directly to https://video.bsky.app
 * 3. Poll getJobStatus until processing completes or fails
 *
 * This avoids the PDS-proxied `agent.app.bsky.video.uploadVideo` call which
 * only works on bsky.social and returns MethodNotImplemented on self-hosted PDSes.
 */

import type { Agent } from '@atproto/api'
import { buildPlcDidDocUrl } from '@mackinac/shared'

export interface UploadVideoOptions {
    onUploadProgress?: (pct: number) => void
    onProcessingProgress?: (pct: number) => void
    signal?: AbortSignal
}

const VIDEO_SERVICE_URL = 'https://video.bsky.app'
const VIDEO_SERVICE_DID = 'did:web:video.bsky.app'
/** URL path for the upload endpoint on video.bsky.app */
const VIDEO_UPLOAD_ENDPOINT = 'app.bsky.video.uploadVideo'
/**
 * Lexicon used in the service auth token's `lxm` scope for uploads.
 * video.bsky.app internally calls `com.atproto.repo.uploadBlob` on the
 * user's PDS using this token, so the delegation must cover that method —
 * NOT `app.bsky.video.uploadVideo`.
 */
const VIDEO_UPLOAD_AUTH_LXM = 'com.atproto.repo.uploadBlob'
const VIDEO_LIMITS_LXM = 'app.bsky.video.getUploadLimits'
const VIDEO_STATUS_LXM = 'app.bsky.video.getJobStatus'
/** Maximum time to wait for video processing (5 minutes) */
const POLL_TIMEOUT_MS = 5 * 60 * 1000
const POLL_INTERVAL_MS = 2_000

interface PlcDocument {
    service?: { id: string; type: string; serviceEndpoint: string }[]
}

/**
 * Resolve a DID to its PDS URL by querying the PLC directory.
 * Returns the PDS service endpoint URL, or null if resolution fails.
 */
async function resolvePdsUrl(did: string): Promise<string | null> {
    try {
        const response = await fetch(buildPlcDidDocUrl(did), { signal: AbortSignal.timeout(5000) })
        if (!response.ok) return null
        const doc: PlcDocument = await response.json()
        const pdsService = doc.service?.find(
            s => s.id === '#atproto_pds' || s.type === 'AtprotoPersonalDataServer'
        )
        const endpoint = pdsService?.serviceEndpoint
        if (!endpoint) return null
        const parsed = new URL(endpoint)
        if (parsed.protocol !== 'https:') return null
        return endpoint
    } catch {
        return null
    }
}

/**
 * Derive the user's PDS DID by resolving their DID document via the PLC directory.
 * The PDS DID follows the did:web scheme: `did:web:<hostname>`.
 * video.bsky.app presents this token to the PDS for validation, so
 * the PDS hostname must be in `aud` — not the video service DID.
 *
 * Unlike `agent.dispatchUrl` (which points to the AppView proxy, e.g. bsky.social),
 * this correctly resolves the user's actual PDS for self-hosted PDS instances.
 * Falls back to `did:web:bsky.social` if PLC directory resolution fails.
 */
export async function getPdsDidForUser(agent: Agent): Promise<string> {
    const did = agent.assertDid
    const pdsUrl = await resolvePdsUrl(did)
    if (pdsUrl) {
        const hostname = new URL(pdsUrl).hostname
        return `did:web:${hostname}`
    }
    return 'did:web:bsky.social'
}

export interface VideoBlobRef {
    $type: string
    ref: { $link: string }
    mimeType: string
    size: number
}

export interface VideoJobStatus {
    jobId: string
    state: string
    progress?: number
    blob?: VideoBlobRef
    message?: string
    error?: string
}

interface UploadLimits {
    canUpload: boolean
    message?: string
}

/**
 * Obtain a short-lived service auth token from the user's own PDS.
 * The token is scoped to the user's PDS DID (not the video service DID)
 * because video.bsky.app presents this token to the PDS when calling
 * uploadBlob on the user's behalf — the PDS must be in `aud` to validate it.
 */
export async function getVideoServiceAuth(agent: Agent): Promise<string> {
    const exp = Math.floor(Date.now() / 1000) + 60 * 60 // 1 hour from now
    const pdsDid = await getPdsDidForUser(agent)
    const response = await agent.com.atproto.server.getServiceAuth({
        aud: pdsDid,
        lxm: VIDEO_UPLOAD_AUTH_LXM,
        exp,
    })
    return response.data.token
}

/**
 * Check daily upload quota via app.bsky.video.getUploadLimits.
 * Returns null if the check itself fails — callers should treat null as
 * "proceed anyway" so a preflight failure never blocks the user.
 * This includes auth failures (e.g. a self-hosted PDS that does not
 * recognize VIDEO_LIMITS_LXM), which are expected on some deployments.
 */
async function checkUploadLimits(agent: Agent): Promise<UploadLimits | null> {
    try {
        const exp = Math.floor(Date.now() / 1000) + 60 * 60
        const authResponse = await agent.com.atproto.server.getServiceAuth({
            aud: VIDEO_SERVICE_DID,
            lxm: VIDEO_LIMITS_LXM,
            exp,
        })
        const token = authResponse.data.token

        const response = await fetch(`${VIDEO_SERVICE_URL}/xrpc/${VIDEO_LIMITS_LXM}`, {
            headers: { Authorization: `Bearer ${token}` },
        })

        if (!response.ok) return null

        const data = await response.json()
        return { canUpload: data.canUpload, message: data.message }
    } catch {
        // Non-fatal — proceed with upload attempt if preflight fails
        return null
    }
}

/**
 * Upload a video file directly to video.bsky.app and poll until processing completes.
 *
 * @returns The processed blob ref to embed in a post
 * @throws On quota exceeded, service auth failure, upload error, job failure, or polling timeout
 */
export async function uploadVideoDirect(
    agent: Agent,
    file: File,
    options?: UploadVideoOptions
): Promise<VideoBlobRef> {
    // Step 0: Preflight — check daily upload quota
    const limits = await checkUploadLimits(agent)
    if (limits !== null && limits.canUpload === false) {
        throw new Error(
            limits.message ?? "You've reached your daily video upload limit. Try again tomorrow."
        )
    }

    // Step 1: Get service auth token from the user's own PDS
    let serviceAuthToken: string
    try {
        serviceAuthToken = await getVideoServiceAuth(agent)
    } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        throw new Error(`Failed to get video service auth token: ${message}`, { cause: err })
    }

    if (options?.signal?.aborted) {
        throw new DOMException('Upload aborted', 'AbortError')
    }

    // Extract the user DID from the agent session
    const did = agent.assertDid

    // Step 2: Upload the raw video bytes directly to video.bsky.app via XHR so
    // xhr.upload.onprogress fires for byte-level upload progress reporting.
    const videoBytes = await file.arrayBuffer()

    if (options?.signal?.aborted) {
        throw new DOMException('Upload aborted', 'AbortError')
    }

    const params = new URLSearchParams({ did, name: file.name })
    const uploadUrl = `${VIDEO_SERVICE_URL}/xrpc/${VIDEO_UPLOAD_ENDPOINT}?${params.toString()}`

    interface XhrResult {
        status: number
        body: string | null
    }

    let xhrResult: XhrResult
    try {
        xhrResult = await new Promise<XhrResult>((resolve, reject) => {
            const xhr = new XMLHttpRequest()

            if (options?.signal?.aborted) {
                reject(new DOMException('Upload aborted', 'AbortError'))
                return
            }

            const onAbort = () => {
                xhr.abort()
                reject(new DOMException('Upload aborted', 'AbortError'))
            }
            options?.signal?.addEventListener('abort', onAbort, { once: true })

            xhr.upload.onprogress = (e: ProgressEvent) => {
                if (e.lengthComputable && options?.onUploadProgress) {
                    options.onUploadProgress(Math.round((e.loaded / e.total) * 100))
                }
            }

            xhr.onload = () => {
                options?.signal?.removeEventListener('abort', onAbort)
                resolve({ status: xhr.status, body: xhr.responseText || null })
            }
            xhr.onerror = () => {
                options?.signal?.removeEventListener('abort', onAbort)
                reject(new Error('Video upload network error: XHR request failed'))
            }

            xhr.open('POST', uploadUrl)
            xhr.setRequestHeader('Authorization', `Bearer ${serviceAuthToken}`)
            xhr.setRequestHeader('Content-Type', file.type || 'video/mp4')
            xhr.send(videoBytes)
        })
    } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') throw err
        throw err instanceof Error ? err : new Error(`Video upload network error: ${String(err)}`)
    }

    // Parse the response body regardless of HTTP status — video.bsky.app can return
    // non-2xx codes (e.g. 409 already_exists) with a valid JobStatus body that includes
    // a jobId. Gating on status would throw before we ever see the jobId.
    let uploadBody: Record<string, unknown> | null = null
    try {
        if (xhrResult.body) uploadBody = JSON.parse(xhrResult.body)
    } catch {
        // Not valid JSON — fall through; absence of jobId below will throw
    }

    // Support both flat JobStatus (e.g. already_exists) and wrapped { jobStatus: {...} }
    const rawUploadStatus: VideoJobStatus | undefined =
        uploadBody?.jobId != null
            ? (uploadBody as unknown as VideoJobStatus)
            : (uploadBody?.jobStatus as VideoJobStatus | undefined)
    const jobId: string | undefined = rawUploadStatus?.jobId

    if (!jobId) {
        const errMsg = uploadBody?.error
            ? `${uploadBody.error}${uploadBody.message ? ': ' + String(uploadBody.message) : ''}`
            : `HTTP ${xhrResult.status}`
        throw new Error(errMsg)
    }

    // Step 3: Poll getJobStatus until completion or failure
    const deadline = Date.now() + POLL_TIMEOUT_MS
    const statusUrl = `${VIDEO_SERVICE_URL}/xrpc/${VIDEO_STATUS_LXM}`

    // rawUploadStatus is defined here because jobId (derived from it) is truthy
    let jobStatus: VideoJobStatus = rawUploadStatus!

    while (
        jobStatus.state !== 'JOB_STATE_COMPLETED' &&
        jobStatus.state !== 'JOB_STATE_FAILED' &&
        jobStatus.state !== 'JOB_STATE_ERROR'
    ) {
        if (options?.signal?.aborted) {
            throw new DOMException('Upload aborted', 'AbortError')
        }

        if (Date.now() >= deadline) {
            throw new Error('Video processing timed out — please try again')
        }

        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS))

        if (options?.signal?.aborted) {
            throw new DOMException('Upload aborted', 'AbortError')
        }

        let statusResponse: Response
        try {
            statusResponse = await fetch(`${statusUrl}?jobId=${encodeURIComponent(jobId)}`, {
                signal: options?.signal,
            })
        } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') throw err
            const message = err instanceof Error ? err.message : String(err)
            throw new Error(`Failed to poll video job status: ${message}`, { cause: err })
        }

        if (!statusResponse.ok) {
            const body = await statusResponse.text().catch(() => '')
            throw new Error(
                `Video status poll failed (${statusResponse.status}): ${body || statusResponse.statusText}`
            )
        }

        const statusData = await statusResponse.json()
        if (!statusData?.jobStatus?.jobId) {
            throw new Error('Video status poll returned an unexpected response shape')
        }
        jobStatus = statusData.jobStatus

        if (typeof jobStatus.progress === 'number' && options?.onProcessingProgress) {
            options.onProcessingProgress(jobStatus.progress)
        }
    }

    if (jobStatus.state === 'JOB_STATE_FAILED' || jobStatus.state === 'JOB_STATE_ERROR') {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty message/error should fall through to the default text, not throw a blank error
        throw new Error(jobStatus.message || jobStatus.error || 'Video processing failed')
    }

    // If completed but blob not yet in the response (e.g. already_exists short-circuit),
    // fetch once via getJobStatus to retrieve the blob.
    if (!jobStatus.blob) {
        let blobStatusResponse: Response
        try {
            blobStatusResponse = await fetch(`${statusUrl}?jobId=${encodeURIComponent(jobId)}`, {
                signal: options?.signal,
            })
        } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') throw err
            const message = err instanceof Error ? err.message : String(err)
            throw new Error(`Failed to poll video job status: ${message}`, { cause: err })
        }
        if (!blobStatusResponse.ok) {
            const body = await blobStatusResponse.text().catch(() => '')
            throw new Error(
                `Video status poll failed (${blobStatusResponse.status}): ${body || blobStatusResponse.statusText}`
            )
        }
        const blobStatusData = await blobStatusResponse.json()
        if (!blobStatusData?.jobStatus?.jobId) {
            throw new Error('Video status poll returned an unexpected response shape')
        }
        jobStatus = blobStatusData.jobStatus
    }

    if (!jobStatus.blob) {
        throw new Error('Video processing completed but no blob was returned')
    }

    return jobStatus.blob as VideoBlobRef
}
